import React, { useMemo, useState } from 'react';
import { Bell, CreditCard, LockKeyhole, X } from 'lucide-react';

import { buildMarketingTrialUrl } from '../lib/marketingTrialUrl';
import './PremiumAccessPaywall.css';

type PremiumAccessPaywallPlan = {
  slug: string;
  term: string;
  price: string;
  dailyEquivalent: string;
};

type PremiumAccessPaywallProps = {
  sourcePage: string;
  trialCtaId: string;
  trialCtaLocation: string;
  trialMedium: string;
  backHref?: string;
  backLabel: string;
  onBack?: () => void;
};

const trialPlans: PremiumAccessPaywallPlan[] = [
  { slug: 'weekly', term: 'week', price: '$29', dailyEquivalent: '$4.14/day' },
  { slug: 'three-months', term: '3 months', price: '$299', dailyEquivalent: '$3.27/day' },
  { slug: 'six-months', term: '6 months', price: '$499', dailyEquivalent: '$2.73/day' },
];

const PremiumAccessPaywall: React.FC<PremiumAccessPaywallProps> = ({
  sourcePage,
  trialCtaId,
  trialCtaLocation,
  trialMedium,
  backHref,
  backLabel,
  onBack,
}) => {
  const [selectedPlanSlug, setSelectedPlanSlug] = useState('six-months');
  const selectedPlan = trialPlans.find((plan) => plan.slug === selectedPlanSlug) || trialPlans[0];

  const trialUrl = useMemo(() => {
    const url = new URL(
      buildMarketingTrialUrl({
        ctaId: trialCtaId,
        ctaLocation: trialCtaLocation,
        sourcePage,
        medium: trialMedium,
      })
    );
    url.searchParams.set('selected_plan', selectedPlan.slug);
    return url.toString();
  }, [selectedPlan.slug, sourcePage, trialCtaId, trialCtaLocation, trialMedium]);

  const closeControl = onBack ? (
    <button
      type="button"
      className="qcl-start-trial-close"
      aria-label={backLabel}
      title={backLabel}
      onClick={onBack}
    >
      <X size={16} strokeWidth={2.4} />
    </button>
  ) : (
    <a
      className="qcl-start-trial-close"
      href={backHref || '#'}
      aria-label={backLabel}
      title={backLabel}
    >
      <X size={16} strokeWidth={2.4} />
    </a>
  );

  return (
    <div className="qcl-start-trial-fullscreen">
      <section
        className="qcl-start-trial-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="premium-access-paywall-title"
      >
        {closeControl}
        <div className="qcl-start-trial-brand">QCL</div>
        <h2 id="premium-access-paywall-title">How your free trial works</h2>
        <p>No surprises. We&apos;ll remind you before billing.</p>

        <div className="qcl-trial-timeline" aria-label="Free trial timeline">
          <div className="qcl-trial-timeline-item today">
            <div className="qcl-trial-timeline-icon" aria-hidden="true">
              <LockKeyhole size={24} strokeWidth={2.3} />
            </div>
            <div>
              <span>Today</span>
              <strong>Get full access - $0</strong>
              <p>Unlock everything in QCL. No charge today.</p>
            </div>
          </div>

          <div className="qcl-trial-timeline-item reminder">
            <div className="qcl-trial-timeline-icon" aria-hidden="true">
              <Bell size={24} strokeWidth={2.3} />
            </div>
            <div>
              <span>Day 5</span>
              <strong>We email a reminder</strong>
              <p>Two days before your trial ends, in case you want to cancel.</p>
            </div>
          </div>

          <div className="qcl-trial-timeline-item billing">
            <div className="qcl-trial-timeline-icon" aria-hidden="true">
              <CreditCard size={24} strokeWidth={2.3} />
            </div>
            <div>
              <span>Day 8</span>
              <strong>We charge {selectedPlan.price}</strong>
              <p>Then every {selectedPlan.term} until you cancel. Stop anytime.</p>
            </div>
          </div>
        </div>

        <div className="qcl-start-trial-billing-label">After trial, bill me</div>
        <div className="qcl-start-trial-options" aria-label="Trial billing options" role="radiogroup">
          {trialPlans.map((plan) => {
            const isSelected = plan.slug === selectedPlan.slug;
            const isRecommended = plan.slug === 'six-months';
            return (
              <button
                key={plan.slug}
                type="button"
                role="radio"
                aria-checked={isSelected}
                className={`qcl-start-trial-option${isRecommended ? ' featured' : ''}${isSelected ? ' selected' : ''}`}
                onClick={() => setSelectedPlanSlug(plan.slug)}
              >
                {isRecommended ? <span className="qcl-start-trial-best-value">Best value</span> : null}
                <span>{plan.term}</span>
                <strong>{plan.price}</strong>
                <small>{plan.dailyEquivalent}</small>
              </button>
            );
          })}
        </div>

        <a className="qcl-start-trial-submit" href={trialUrl}>
          Start 7-day free trial
        </a>
      </section>
    </div>
  );
};

export default PremiumAccessPaywall;
